import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Icon, Switch } from 'components';
import { Size } from 'design/types';
import { ILabor, ILaborLocation, IPOSLaborEmployee, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { EmployeeSearch } from './EmployeeSearch';
import { EmployeeShiftCard } from './EmployeeShiftCard';
import { OverrideWarningModal } from './OverrideWarningModal';
import {
  BoardEmptyMessage,
  BoardEmptyState,
  EmployeeListLayout,
  MediumButton,
  Notice,
  NoticeMessage,
  NoticeTitle,
  Separator,
  TaskHeader,
  TaskSubtitle,
  TaskTitle,
  TaskTitles,
  ToolbarCount,
  ToolbarDateTag,
  ToolbarFilters,
  ToolbarRight,
  ToolbarRow,
  ToolbarSearch,
  ToolbarSwitch,
  ToolbarSwitchLabel,
} from './styles';
import { formatBusinessDate, getEmployeeLocationKey, hasLaborData, joinLaborWithEmployees, laborNeedsReview, searchEmployees } from './utils';

interface ILaborReviewBoardProps {
  siteId: string;
  date: string;
  locationName?: string;
  laborRecords: ILabor[];
  // The primary location first.
  laborLocations: ILaborLocation[];
  posEmployees: IPOSLaborEmployee[];
  jobTitles: string[];
  permissions?: PermissionType[];
  onBackToTipCalculator: () => void;
}

export const LaborReviewBoard: React.FC<ILaborReviewBoardProps> = ({ siteId, date, locationName, laborRecords, laborLocations, posEmployees, jobTitles, permissions, onBackToTipCalculator }) => {
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const [searchQuery, setSearch] = useState('');
  const [showOverrideWarning, setShowOverrideWarning] = useState(true);
  const canUpdateLaborData = hasSitePermission(permissions, PermissionType.INTEGRATION_POS_LABOR_DATA_UPDATE);

  const employees = useMemo(() => {
    const allEmployees = joinLaborWithEmployees({ laborRecords, posEmployees, locations: laborLocations });
    // A search looks through every employee, as if "Show all employees" were on.
    if (searchQuery) return searchEmployees({ employees: allEmployees, query: searchQuery });
    return showAllEmployees ? allEmployees : allEmployees.filter(hasLaborData);
  }, [laborRecords, posEmployees, laborLocations, showAllEmployees, searchQuery]);

  const needsReviewCount = useMemo(() => laborRecords.filter(laborNeedsReview).length, [laborRecords]);

  return (
    <>
      {canUpdateLaborData && showOverrideWarning && <OverrideWarningModal onClose={() => setShowOverrideWarning(false)} />}

      <TaskHeader>
        <TaskTitles>
          <TaskTitle>Legion Labor Override Tool</TaskTitle>
          <TaskSubtitle data-cy="labor-review-subtitle">
            <span>{locationName}</span> <Separator>·</Separator> <span>{formatBusinessDate(date)}</span>
          </TaskSubtitle>
        </TaskTitles>
        <MediumButton appearance="white" size="small" label="Back to Tip Calculator" onClick={onBackToTipCalculator} data-cy="back-to-tip-calculator-button" />
      </TaskHeader>

      <ToolbarRow>
        <ToolbarSearch>
          <EmployeeSearch onSearch={setSearch} />
        </ToolbarSearch>
        <ToolbarRight>
          <ToolbarFilters>
            <ToolbarSwitch>
              <ToolbarSwitchLabel>Show all employees</ToolbarSwitchLabel>
              <Switch checked={showAllEmployees || Boolean(searchQuery)} disabled={Boolean(searchQuery)} onChange={() => setShowAllEmployees((prev) => !prev)} />
            </ToolbarSwitch>
            <ToolbarDateTag label={dayjs(date).format('ddd, MMM D, YYYY')} size={Size.SMALL} capitalize={false} />
          </ToolbarFilters>
          <ToolbarCount data-cy="employee-count">
            {employees.length} employee{employees.length === 1 ? '' : 's'}
          </ToolbarCount>
        </ToolbarRight>
      </ToolbarRow>

      {needsReviewCount > 0 && (
        <Notice data-cy="needs-review-notice">
          <NoticeTitle data-cy="needs-review-notice-title">{needsReviewCount === 1 ? '1 employee has' : `${needsReviewCount} employees have`} incomplete labor data</NoticeTitle>
          <NoticeMessage>A shift missing a clock out or a break end can&apos;t be used by the tip calculator. Correct the highlighted shifts before continuing.</NoticeMessage>
        </Notice>
      )}

      {employees.length === 0 ? (
        <BoardEmptyState data-cy="empty-state">
          <Icon icon="CreditCard" />
          <BoardEmptyMessage>{searchQuery ? 'No employees match your search.' : 'No labor data received from Legion for this business day.'}</BoardEmptyMessage>
        </BoardEmptyState>
      ) : (
        <EmployeeListLayout>
          {employees.map((employee) => (
            <EmployeeShiftCard
              key={getEmployeeLocationKey(employee.location.id, employee.employeeId)}
              siteId={siteId}
              date={date}
              location={employee.location}
              employeeId={employee.employeeId}
              employeeName={employee.name}
              labor={employee.labor}
              jobTitles={jobTitles}
              permissions={permissions}
            />
          ))}
        </EmployeeListLayout>
      )}
    </>
  );
};
